import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useConversationStoreShallow } from 'src/store/conversationStore';
import * as yup from 'yup';

import { ConversationLayout } from '@components/templates/ConversationLayout/ConversationLayout';

import { useSubmitCaseReport } from '@utils/hooks/useReportCaseSubmit';

import { conversationSubmitLoadingVar } from '@boot/reactiveVariables';
import { ReportMailAndPinForm } from '@domains/new-conversation/organisms/ReportMailAndPinForm/ReportMailAndPinForm';
import { ProceedButton } from '@domains/new-conversation/pages/Conversation.styled';
import { useConversationRedirection } from '@domains/new-conversation/useConversationRedirection';
import { yupResolver } from '@hookform/resolvers/yup';

import { ContentWrapper } from './ReportSetPinPage.styled';

export const ReportSetPinPage = () => {
    useConversationRedirection();

    const { t } = useTranslation();
    const { caseUuid } = useParams<{ caseUuid: string }>();

    const methods = useForm<ReportMailAndPinFormValues>({
        mode: 'onChange',
        resolver: yupResolver(ReportMailAndPinSchema),
    });

    const {
        formState: { isSubmitted, isSubmitting, isValid },
        handleSubmit,
    } = methods;

    const { loading: conversationLoading } = useConversationStoreShallow([
        'loading',
    ]);

    const [submitCaseReport, { loading }] = useSubmitCaseReport();

    const handleFormSubmit = handleSubmit(
        (data: ReportMailAndPinFormValues) => {
            conversationSubmitLoadingVar(true);

            void submitCaseReport({
                variables: {
                    input: {
                        uuid: caseUuid ?? '',
                        /*
                         * Sent raw even when unconfirmed - deliberately. The
                         * cases service re-checks the address against the
                         * reporter's own session and DISCARDS an unconfirmed
                         * one, writing an explicit null that also clears any
                         * address the report already stored. Nulling it out
                         * client-side would flip that verdict: to the backend
                         * a missing address means "the reporter supplied
                         * none - leave the stored address alone", so it needs
                         * the raw value for its own bookkeeping.
                         */
                        guestEmail: data.email || undefined,
                        pinCode: data.pin,
                    },
                },
            });
        },
    );

    return (
        <ConversationLayout
            sidebarProps={{
                buttons: (
                    <ProceedButton
                        disabled={
                            (!isValid && isSubmitted) || conversationLoading
                        }
                        isLoading={loading || isSubmitting}
                        onClick={handleFormSubmit}
                        testId="button_submit-report"
                        variant={'primary'}
                    >
                        {t('SUBMIT_REPORT')}
                    </ProceedButton>
                ),
                currentStep: 4,
                preventionModalActive: true,
            }}
        >
            <ContentWrapper>
                <FormProvider {...methods}>
                    <ReportMailAndPinForm />
                </FormProvider>
            </ContentWrapper>
        </ConversationLayout>
    );
};

const ReportMailAndPinSchema = yup.object({
    email: yup.string().email('INVALID_EMAIL'),
    pin: yup.string().min(6, 'INVALID_PIN').required('FIELD_CANNOT_BE_EMPTY'),
});

export type ReportMailAndPinFormValues = yup.InferType<
    typeof ReportMailAndPinSchema
>;
